import React, { useState, useEffect } from 'react';
import '../styles/BookNow.css';
import { toast } from 'react-toastify';
import { FaWhatsapp } from 'react-icons/fa'; // Import WhatsApp icon

function BookNow() {
  // Helper function to round current time to nearest next 30 minutes (IST) and return both 24-hour and 12-hour formats
  const getRoundedCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const roundedMinutes = minutes < 30 ? 30 : 0;
    const adjustedHours = minutes < 30 ? hours : hours + 1;
    const adjustedMinutes = roundedMinutes;
    const time24 = `${adjustedHours.toString().padStart(2, '0')}:${adjustedMinutes.toString().padStart(2, '0')}`;
    
    // Convert to 12-hour format for UI
    const time12 = convertTo12Hour(time24);
    
    return { time24, time12 };
  };

  // Helper function to convert 12-hour format to 24-hour format
  const convertTo24Hour = (time12) => {
    const [time, period] = time12.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    if (time === '00:00' && period === 'AM') hours = 24; // Handle midnight
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Helper function to convert 24-hour format to 12-hour format
  const convertTo12Hour = (time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    if (hours === 24) return `00:00 AM`; // Handle midnight case
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Generate time options in 30-minute intervals in 12-hour format
  const generateTimeOptions = () => {
    const times = [];
    for (let hours = 0; hours <= 24; hours++) { // Include 24:00 for midnight
      for (let minutes of [0, 30]) {
        const time24 = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        const time12 = convertTo12Hour(time24);
        times.push(time12);
      }
    }
    return times;
  };

  // Helper function to calculate To Date based on pricing type
  const calculateToDate = (fromDate, pricingType) => {
    if (!fromDate) return '';
    const date = new Date(fromDate);
    
    switch (pricingType) {
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      case 'monthly': {
        const originalDay = date.getDate();
        date.setMonth(date.getMonth() + 1);
        if (date.getDate() !== originalDay) {
          date.setDate(0);
        }
        break;
      }
      default:
        return '';
    }
    
    return date.toISOString().split('T')[0];
  };

  // Set default date-time filters
  const defaultDateFrom = new Date().toISOString().split('T')[0];
  const defaultDateTo = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const defaultTime = getRoundedCurrentTime();

  // WhatsApp configuration
  const WHATSAPP_PHONE_NUMBER = '+919894150416'; // Replace with your phone number

  const [vehicles, setVehicles] = useState({ available: [], unavailable: [] });
  const [filteredVehicles, setFilteredVehicles] = useState({ available: [], unavailable: [] });
  const [states, setStates] = useState(['all']);
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState(['all']);
  const [categories, setCategories] = useState(['all']);
  const [stateFilter, setStateFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priceFilterType, setPriceFilterType] = useState('hourly');
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [isLoading, setIsLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
    timeFrom: defaultTime.time24,
    timeTo: defaultTime.time24,
  });
  const [timeDisplay, setTimeDisplay] = useState({
    timeFrom: defaultTime.time12,
    timeTo: defaultTime.time12,
  });
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [bookingAsGuest, setBookingAsGuest] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contact: '',
    email: '',
    hasLicense: false,
    licenseFile: null,
    aadharFile: null,
    agreeTerms: false,
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
    timeFrom: defaultTime.time24,
    timeTo: defaultTime.time24,
  });
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState('');
  const [alternatives, setAlternatives] = useState([]);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [orderId, setOrderId] = useState(null);
  const [isVehiclesLoading, setIsVehiclesLoading] = useState(true);

  // Price range configurations
  const priceConfigs = {
    hourly: { min: 0, max: 1000, step: 10 },
    weekly: { min: 0, max: 20000, step: 100 },
    monthly: { min: 0, max: 50000, step: 500 },
  };

  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/profile`, {
          credentials: 'include',
        });
        if (response.ok) {
          setIsLoggedIn(true);
        } else {
          setIsLoggedIn(false);
        }
      } catch (err) {
        console.error('Auth check error:', err);
        setIsLoggedIn(false);
      }
    };
    checkAuth();
  }, []);

  // Fetch data (vehicles, cities, categories)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsVehiclesLoading(true);
        const citiesResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/cities`, {
          credentials: 'include',
        });
        if (!citiesResponse.ok) throw new Error('Failed to fetch cities');
        const citiesData = await citiesResponse.json();
        const uniqueStates = ['all', ...new Set(citiesData.map((city) => city.state))];
        setStates(uniqueStates);
        setCities(citiesData);
        setFilteredCities(['all', ...citiesData.map((city) => city.city)]);

        const categoriesResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/categories`, {
          credentials: 'include',
        });
        if (!categoriesResponse.ok) throw new Error('Failed to fetch categories');
        const categoriesData = await categoriesResponse.json();
        setCategories(['all', ...categoriesData.map((cat) => cat.name)]);

        const dateTo = priceFilterType === 'hourly' ? dateFilter.dateTo : calculateToDate(dateFilter.dateFrom, priceFilterType);
        const timeTo = priceFilterType === 'hourly' ? dateFilter.timeTo : dateFilter.timeFrom;
        const vehiclesResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/vehicles/availability?dateFrom=${dateFilter.dateFrom}&dateTo=${dateTo}&timeFrom=${dateFilter.timeFrom}&timeTo=${timeTo}`,
          { credentials: 'include' }
        );
        if (!vehiclesResponse.ok) throw new Error('Failed to fetch vehicles');
        const vehiclesData = await vehiclesResponse.json();
        setVehicles(vehiclesData);
        setFilteredVehicles(vehiclesData);
      } catch (err) {
        setError('Failed to load data. Please try again.');
        console.error(err);
      } finally{
        setIsVehiclesLoading(false);
      }
    };
    fetchData();
  }, [dateFilter, priceFilterType]);

  // Update filtered cities when stateFilter changes
  useEffect(() => {
    if (stateFilter === 'all') {
      setFilteredCities(['all', ...cities.map((city) => city.city)]);
    } else {
      const filtered = cities.filter((city) => city.state === stateFilter).map((city) => city.city);
      setFilteredCities(['all', ...filtered]);
      if (!filtered.includes(cityFilter)) setCityFilter('all');
    }
  }, [stateFilter, cities, cityFilter]);

  // Filter vehicles based on state, city, category, price
  useEffect(() => {
    let filtered = { available: [], unavailable: [] };

    const filterVehicles = (vehicleList) =>
      vehicleList.filter((vehicle) => {
        let matches = true;
        if (stateFilter !== 'all') {
          const validCities = cities.filter((city) => city.state === stateFilter).map((city) => city.city);
          matches = matches && validCities.includes(vehicle.city);
        }
        if (cityFilter !== 'all') {
          matches = matches && vehicle.city === cityFilter;
        }
        if (categoryFilter !== 'all') {
          matches = matches && vehicle.type === categoryFilter;
        }
        if (priceFilterType) {
          matches =
            matches &&
            vehicle.prices[priceFilterType] >= priceRange[0] &&
            vehicle.prices[priceFilterType] <= priceRange[1];
        }
        return matches;
      });

    filtered.available = filterVehicles(vehicles.available);
    filtered.unavailable = filterVehicles(vehicles.unavailable);

    setFilteredVehicles(filtered);
  }, [stateFilter, cityFilter, categoryFilter, priceFilterType, priceRange, vehicles, cities]);

  // Fetch unavailable dates for selected vehicle
  useEffect(() => {
    const fetchUnavailableDates = async () => {
      if (selectedVehicle) {
        try {
          const dateTo = priceFilterType === 'hourly' ? formData.dateTo : calculateToDate(formData.dateFrom, priceFilterType);
          const timeTo = priceFilterType === 'hourly' ? formData.timeTo : formData.timeFrom;
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/vehicles/${selectedVehicle.id}?dateFrom=${formData.dateFrom}&dateTo=${dateTo}&timeFrom=${formData.timeFrom}&timeTo=${timeTo}`,
            { credentials: 'include' }
          );
          if (!response.ok) throw new Error('Failed to fetch vehicle details');
          const vehicleData = await response.json();
          setUnavailableDates(vehicleData.unavailableDates || []);
        } catch (err) {
          console.error('Error fetching unavailable dates:', err);
        }
      }
    };
    fetchUnavailableDates();
  }, [selectedVehicle, formData.dateFrom, formData.timeFrom, priceFilterType, formData.dateTo, formData.timeTo]);

  const handlePriceChange = (index, value) => {
    setPriceRange((prev) => (index === 0 ? [value, prev[1]] : [prev[0], value]));
  };

  const handlePriceFilterTypeChange = (type) => {
    setPriceFilterType(type);
    setPriceRange([priceConfigs[type].min, priceConfigs[type].max]);
    setDateFilter((prev) => ({
      ...prev,
      dateTo: type === 'hourly' ? defaultDateTo : '',
      timeTo: type === 'hourly' ? defaultTime.time24 : '',
    }));
    setTimeDisplay((prev) => ({
      ...prev,
      timeTo: type === 'hourly' ? defaultTime.time12 : '',
    }));
    setFormData((prev) => ({
      ...prev,
      dateTo: type === 'hourly' ? defaultDateTo : '',
      timeTo: type === 'hourly' ? defaultTime.time24 : '',
    }));
  };

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'timeFrom' || name === 'timeTo') {
      const time24 = convertTo24Hour(value);
      setDateFilter((prev) => {
        const newFilter = { ...prev, [name]: time24 };
        if (name === 'dateFrom') {
          newFilter.dateTo = '';
          newFilter.timeTo = '';
        } else if (name === 'timeFrom' && (priceFilterType === 'weekly' || priceFilterType === 'monthly')) {
          newFilter.timeTo = '';
        }
        return newFilter;
      });
      setTimeDisplay((prev) => ({
        ...prev,
        [name]: value,
        ...(name === 'timeFrom' && (priceFilterType === 'weekly' || priceFilterType === 'monthly') ? { timeTo: '' } : {}),
      }));
      setFormData((prev) => ({
        ...prev,
        [name]: time24,
        ...(name === 'dateFrom' ? { dateTo: '', timeTo: '' } : {}),
        ...(name === 'timeFrom' && (priceFilterType === 'weekly' || priceFilterType === 'monthly') ? { timeTo: '' } : {}),
      }));
    } else {
      setDateFilter((prev) => {
        const newFilter = { ...prev, [name]: value };
        if (name === 'dateFrom') {
          newFilter.dateTo = '';
          newFilter.timeTo = '';
        }
        return newFilter;
      });
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        ...(name === 'dateFrom' ? { dateTo: '', timeTo: '' } : {}),
      }));
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else if (type === 'file') {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
    } else if (name === 'timeFrom' || name === 'timeTo') {
      const time24 = convertTo24Hour(value);
      setFormData((prev) => ({
        ...prev,
        [name]: time24,
      }));
      setTimeDisplay((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleRemoveFile = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: null,
    }));
  };

  const downloadTermsImage = () => {
    const link = document.createElement('a');
    link.href = '/assets/images/agreementterms.jpeg';
    link.download = 'agreementterms.jpeg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const calculateDuration = (dateFrom, dateTo, timeFrom, timeTo) => {
    if (!dateFrom || !dateTo || !timeFrom || !timeTo) return 0;

    const start = new Date(`${dateFrom}T${timeFrom}:00+05:30`);
    const end = new Date(`${dateTo}T${timeTo}:00+05:30`);
    if (end <= start) return 0;

    const milliseconds = end - start;
    switch (priceFilterType) {
      case 'hourly':
        return Math.ceil(milliseconds / (1000 * 60 * 60));
      case 'weekly':
        return Math.ceil(milliseconds / (1000 * 60 * 60 * 24 * 7));
      case 'monthly':
        return Math.ceil(milliseconds / (1000 * 60 * 60 * 24 * 30));
      default:
        return 0;
    }
  };

  const calculateTotalPrice = (dateFrom, dateTo, timeFrom, timeTo) => {
    const duration = calculateDuration(dateFrom, dateTo, timeFrom, timeTo);
    const price = selectedVehicle?.prices[priceFilterType] || 0;
    const basePrice = duration * price;
    const gst = basePrice * 0.05;
    return (basePrice + gst).toFixed(2);
  };

  // Construct WhatsApp message with booking details
  const constructWhatsAppMessage = () => {
    const {
      name,
      address,
      contact,
      email,
      hasLicense,
      licenseFile,
      aadharFile,
      agreeTerms,
      dateFrom,
      timeFrom,
      dateTo,
      timeTo,
    } = formData;
    const endDate = priceFilterType === 'hourly' ? dateTo : calculateToDate(dateFrom, priceFilterType);
    const endTime = priceFilterType === 'hourly' ? timeTo : timeFrom;
    const duration = calculateDuration(dateFrom, endDate, timeFrom, endTime);
    const totalPrice = calculateTotalPrice(dateFrom, endDate, timeFrom, endTime);

    const message = `
Booking Request:
Vehicle: ${selectedVehicle?.name || 'Not specified'}
Location: ${selectedVehicle?.city || 'Not specified'}
Pricing Type: ${priceFilterType.charAt(0).toUpperCase() + priceFilterType.slice(1)}
Start Date: ${dateFrom || 'Not specified'}
Start Time: ${timeFrom || 'Not specified'}
End Date: ${endDate || 'Not specified'}
End Time: ${endTime || 'Not specified'}
Duration: ${duration} ${priceFilterType}
Total Price (with 5% GST): ₹${totalPrice}
Name: ${name || 'Not specified'}
Address: ${address || 'Not specified'}
Contact: ${contact || 'Not specified'}
Email: ${email || 'Not specified'}
Has Valid License: ${hasLicense ? 'Yes' : 'No'}
License File: ${licenseFile ? licenseFile.name : 'Not uploaded'}
Aadhar File: ${aadharFile ? aadharFile.name : 'Not uploaded'}
Agreed to Terms: ${agreeTerms ? 'Yes' : 'No'}
    `.trim();
    return encodeURIComponent(message);
  };

  // Handle WhatsApp button click
  const handleWhatsAppBooking = () => {
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      const whatsappUrl = `https://wa.me/${WHATSAPP_PHONE_NUMBER}?text=${constructWhatsAppMessage()}`;
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Please fill all required fields correctly before booking via WhatsApp.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const isDateDisabled = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return unavailableDates.some((range) => {
      const from = new Date(range.from).toISOString().split('T')[0];
      const to = new Date(range.to).toISOString().split('T')[0];
      return dateStr >= from && dateStr <= to;
    });
  };

  const openBookingModal = async (vehicle) => {
    try {
      const dateTo = priceFilterType === 'hourly' ? dateFilter.dateTo : calculateToDate(dateFilter.dateFrom, priceFilterType);
      const timeTo = priceFilterType === 'hourly' ? dateFilter.timeTo : dateFilter.timeFrom;

      const availabilityResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/vehicles/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vehicleId: vehicle.id,
          dateFrom: dateFilter.dateFrom,
          dateTo,
          timeFrom: dateFilter.timeFrom,
          timeTo,
        }),
        credentials: 'include',
      });

      const availabilityData = await availabilityResponse.json();

      if (!availabilityResponse.ok || !availabilityData.available) {
        toast.error(
          availabilityData.message || 'This vehicle is not available for the selected dates and times',
          {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        setAlternatives(availabilityData.alternatives || []);
        return;
      }

      setSelectedVehicle(vehicle);
      setBookingAsGuest(true);
      setFormData((prev) => ({
        ...prev,
        dateFrom: dateFilter.dateFrom,
        dateTo,
        timeFrom: dateFilter.timeFrom,
        timeTo,
      }));
      setTimeDisplay((prev) => ({
        ...prev,
        timeFrom: convertTo12Hour(dateFilter.timeFrom),
        timeTo: convertTo12Hour(timeTo),
      }));
      setIsBookingModalOpen(true);
    } catch (err) {
      toast.error('Error checking availability. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      console.error('Error checking availability:', err);
    }
  };

  const closeBookingModal = () => {
    setIsBookingModalOpen(false);
    setFormData({
      name: '',
      address: '',
      contact: '',
      email: '',
      hasLicense: false,
      licenseFile: null,
      aadharFile: null,
      agreeTerms: false,
      dateFrom: defaultDateFrom,
      dateTo: defaultDateTo,
      timeFrom: defaultTime.time24,
      timeTo: defaultTime.time24,
    });
    setTimeDisplay({
      timeFrom: defaultTime.time12,
      timeTo: defaultTime.time12,
    });
    setFormErrors({});
    setBookingAsGuest(true);
    setUnavailableDates([]);
    setError('');
    setAlternatives([]);
  };

  const closeSuccessModal = () => {
    setIsSuccessModalOpen(false);
    closeBookingModal();
    const fetchVehicles = async () => {
      try {
        const dateTo = priceFilterType === 'hourly' ? dateFilter.dateTo : calculateToDate(dateFilter.dateFrom, priceFilterType);
        const timeTo = priceFilterType === 'hourly' ? dateFilter.timeTo : dateFilter.timeFrom;
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/vehicles/availability?dateFrom=${dateFilter.dateFrom}&dateTo=${dateTo}&timeFrom=${dateFilter.timeFrom}&timeTo=${timeTo}`,
          { credentials: 'include' }
        );
        if (!response.ok) throw new Error('Failed to fetch vehicles');
        const vehiclesData = await response.json();
        setVehicles(vehiclesData);
        setFilteredVehicles(vehiclesData);
      } catch (err) {
        setError('Failed to refresh vehicles.');
        console.error(err);
      }
    };
    fetchVehicles();
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.address.trim()) errors.address = 'Address is required';
    if (!formData.contact.trim() || !/^\d{10}$/.test(formData.contact))
      errors.contact = 'Valid 10-digit contact number is required';
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) errors.email = 'Valid email is required';
    if (!formData.dateFrom) errors.dateFrom = 'Start date is required';
    if (!formData.timeFrom) errors.timeFrom = 'Start time is required';
    if (priceFilterType === 'hourly') {
      if (!formData.dateTo) errors.dateTo = 'End date is required';
      if (!formData.timeTo) errors.timeTo = 'End time is required';
      if (
        formData.dateFrom &&
        formData.dateTo &&
        formData.timeFrom &&
        formData.timeTo &&
        new Date(`${formData.dateTo}T${formData.timeTo}:00+05:30`) <=
          new Date(`${formData.dateFrom}T${formData.timeFrom}:00+05:30`)
      ) {
        errors.dateTo = 'End date/time must be after start date/time';
      }
    }
    if (!formData.hasLicense) errors.hasLicense = 'You must confirm you have a valid license';
    if (!formData.licenseFile) errors.licenseFile = 'License upload is required';
    if (!formData.aadharFile) errors.aadharFile = 'Aadhar upload is required';
    if (!formData.agreeTerms) errors.agreeTerms = 'You must agree to terms and conditions';
    return errors;
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      try {
        setIsLoading(true);
        const submitData = {
          dateFrom: formData.dateFrom,
          dateTo: priceFilterType === 'hourly' ? formData.dateTo : calculateToDate(formData.dateFrom, priceFilterType),
          timeFrom: formData.timeFrom,
          timeTo: priceFilterType === 'hourly' ? formData.timeTo : formData.timeFrom,
        };

        if (isDateDisabled(new Date(submitData.dateFrom)) || (submitData.dateTo && isDateDisabled(new Date(submitData.dateTo)))) {
          throw new Error('Selected or calculated dates are unavailable');
        }

        const availabilityResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/vehicles/check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            vehicleId: selectedVehicle.id,
            dateFrom: submitData.dateFrom,
            dateTo: submitData.dateTo,
            timeFrom: submitData.timeFrom,
            timeTo: submitData.timeTo,
          }),
          credentials: 'include',
        });

        if (!availabilityResponse.ok) {
          const errorData = await availabilityResponse.json();
          setAlternatives(errorData.alternatives || []);
          toast.error(errorData.message || 'Vehicle is not available for the selected dates and times', {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          throw new Error(errorData.message || 'Vehicle is not available for the selected dates');
        }

        const availabilityData = await availabilityResponse.json();
        if (!availabilityData.available) {
          setAlternatives(availabilityData.alternatives || []);
          toast.error('Vehicle is not available for the selected dates and times', {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          throw new Error('Vehicle is not available for the selected dates');
        }

        const formDataToSend = new FormData();
        formDataToSend.append('vehicleId', selectedVehicle.id);
        formDataToSend.append('name', formData.name);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('contact', formData.contact);
        formDataToSend.append('address', formData.address);
        formDataToSend.append('pricingType', priceFilterType);
        formDataToSend.append('dateFrom', submitData.dateFrom);
        formDataToSend.append('dateTo', submitData.dateTo);
        formDataToSend.append('timeFrom', submitData.timeFrom);
        formDataToSend.append('timeTo', submitData.timeTo);
        formDataToSend.append('hasLicense', formData.hasLicense);
        formDataToSend.append('agreeTerms', formData.agreeTerms);
        formDataToSend.append('totalPrice', calculateTotalPrice(
          submitData.dateFrom,
          submitData.dateTo,
          submitData.timeFrom,
          submitData.timeTo
        ));
        formDataToSend.append('licenseFile', formData.licenseFile);
        formDataToSend.append('aadharFile', formData.aadharFile);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/orders`, {
          method: 'POST',
          body: formDataToSend,
          credentials: 'include',
        });

        if (!response.ok) {
          const errorData = await response.json();
          setAlternatives(errorData.alternatives || []);
          toast.error(errorData.message || 'Order creation failed', {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          throw new Error(errorData.message || 'Order creation failed');
        }

        const responseData = await response.json();
        setOrderId(responseData.orderId);
        setIsSuccessModalOpen(true);
      } catch (err) {
        setError(`Booking failed: ${err.message}`);
        console.error('Booking error:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleLoginRedirect = () => {
    window.location.href = '/login';
  };

  // Combine available and unavailable vehicles into a single list
  const vehiclesList = [...filteredVehicles.available, ...filteredVehicles.unavailable];

  // TimeSelect component for 12-hour format with 30-minute intervals
  const TimeSelect = ({ name, value, onChange, disabled, className }) => (
    <select
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={className}
    >
      {generateTimeOptions().map((time) => (
        <option key={time} value={time}>
          {time}
        </option>
      ))}
    </select>
  );

  return (
    <main className="book-main">
      {isLoading && (
        <div className="book-fullpage-loader">
          <div className="book-fullpage-spinner"></div>
          <p>Processing your booking...</p>
        </div>
      )}
      <section className="book-hero-section">
        <div className="book-hero-content">
          <h1 className="book-hero-title">Rent Your Perfect Ride</h1>
          <p className="book-hero-subtitle">
            Explore the beautiful hill stations with our premium bikes and scooters
          </p>
        </div>
      </section>

      <section className="book-content-section">
        <div className="book-section-container">
          <div className="book-filters-container">
            <h2 className="book-section-title">Find Your Ride</h2>
            {error && (
              <div className="book-message book-error">
                {error}
                {alternatives.length > 0 && (
                  <div className="book-alternatives">
                    <p><strong>Try these available vehicles:</strong></p>
                    <ul>
                      {alternatives.map((alt) => (
                        <li key={alt.id}>
                          <button
                            className="book-alternative-link"
                            onClick={() => {
                              const vehicle = vehicles.available.find((v) => v.id === alt.id);
                              if (vehicle) openBookingModal(vehicle);
                            }}
                          >
                            {alt.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="book-filters-grid">
              <div className="book-filter-item">
                <label className="book-filter-label">State</label>
                <select
                  className="book-filter-select"
                  value={stateFilter}
                  onChange={(e) => {
                    setStateFilter(e.target.value);
                    setCityFilter('all');
                  }}
                >
                  {states.map((state) => (
                    <option key={state} value={state}>
                      {state === 'all' ? 'All States' : state}
                    </option>
                  ))}
                </select>
              </div>
              <div className="book-filter-item">
                <label className="book-filter-label">City</label>
                <select
                  className="book-filter-select"
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  disabled={stateFilter === 'all' && filteredCities.length === 1}
                >
                  {filteredCities.map((city) => (
                    <option key={city} value={city}>
                      {city === 'all' ? 'All Cities' : city}
                    </option>
                  ))}
                </select>
              </div>
              <div className="book-filter-item">
                <label className="book-filter-label">Category</label>
                <select
                  className="book-filter-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="book-filter-item">
                <label className="book-filter-label">Price Category</label>
                <select
                  className="book-filter-select"
                  value={priceFilterType}
                  onChange={(e) => handlePriceFilterTypeChange(e.target.value)}
                >
                  <option value="hourly">Hourly</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div className="book-filter-item">
                <label className="book-filter-label">
                  {priceFilterType.charAt(0).toUpperCase() + priceFilterType.slice(1)} Price (₹)
                </label>
                <div className="book-price-range">
                  <input
                    type="range"
                    min={priceConfigs[priceFilterType].min}
                    max={priceConfigs[priceFilterType].max}
                    step={priceConfigs[priceFilterType].step}
                    value={priceRange[0]}
                    onChange={(e) => handlePriceChange(0, parseInt(e.target.value))}
                    className="book-price-slider"
                  />
                  <span className="book-price-separator">to</span>
                  <input
                    type="range"
                    min={priceConfigs[priceFilterType].min}
                    max={priceConfigs[priceFilterType].max}
                    step={priceConfigs[priceFilterType].step}
                    value={priceRange[1]}
                    onChange={(e) => handlePriceChange(1, parseInt(e.target.value))}
                    className="book-price-slider"
                  />
                </div>
                <div className="book-price-values">
                  <span>₹{priceRange[0]}</span>
                  <span>₹{priceRange[1]}</span>
                </div>
              </div>
              <div className="book-filter-item">
                <label className="book-filter-label">Date From</label>
                <input
                  type="date"
                  name="dateFrom"
                  value={dateFilter.dateFrom}
                  onChange={handleDateFilterChange}
                  min={defaultDateFrom}
                  className="book-filter-select"
                />
              </div>
              <div className="book-filter-item">
                <label className="book-filter-label">Time From</label>
                <TimeSelect
                  name="timeFrom"
                  value={timeDisplay.timeFrom}
                  onChange={handleDateFilterChange}
                  disabled={isLoading}
                  className={formErrors.timeFrom ? 'book-filter-select book-modal-error' : 'book-filter-select'}
                />
                {formErrors.timeFrom && <span className="book-modal-error-text">{formErrors.timeFrom}</span>}
              </div>
              {priceFilterType === 'hourly' && (
                <>
                  <div className="book-filter-item">
                    <label className="book-filter-label">Date To</label>
                    <input
                      type="date"
                      name="dateTo"
                      value={dateFilter.dateTo}
                      onChange={handleDateFilterChange}
                      min={dateFilter.dateFrom}
                      className="book-filter-select"
                    />
                  </div>
                  <div className="book-filter-item">
                    <label className="book-filter-label">Time To</label>
                    <TimeSelect
                      name="timeTo"
                      value={timeDisplay.timeTo}
                      onChange={handleDateFilterChange}
                      disabled={isLoading}
                      className={formErrors.timeTo ? 'book-filter-select book-modal-error' : 'book-filter-select'}
                    />
                    {formErrors.timeTo && <span className="book-modal-error-text">{formErrors.timeTo}</span>}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="book-results-container">
            <h2 className="book-section-title">
              Vehicles ({vehiclesList.length})
            </h2>
            {isVehiclesLoading ? (
              <div className="book-loading">
                <div className="book-fullpage-spinner"></div>
                <p>Loading vehicles...</p>
              </div>
            ) :
            vehiclesList.length === 0 ? (
              <div className="book-no-results">
                <h3 className="book-no-results-title">No vehicles available</h3>
                <p className="book-no-results-text">Try adjusting your search criteria.</p>
              </div>
            ) : (
              <div className="book-vehicles-grid">
                {vehiclesList.map((vehicle) => (
                  <div key={vehicle.id} className="book-vehicle-card hover-scale">
                    <img
                      src={vehicle.imageUrl}
                      alt={vehicle.name}
                      className="book-vehicle-image"
                      onError={(e) => (e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found')}
                    />
                    <div className="book-vehicle-content">
                      <div className="book-vehicle-header">
                        <h3 className="book-vehicle-title">{vehicle.name}</h3>
                        <span
                          className={`book-vehicle-badge ${
                            vehicle.availability !== 'Available' ? 'book-vehicle-unavailable' : ''
                          }`}
                        >
                          {vehicle.type}
                        </span>
                      </div>
                      <div className="book-vehicle-details">
                        <span className="book-vehicle-location">{vehicle.city}</span>
                      </div>
                      <div className="book-vehicle-prices">
                        <span>Hourly: ₹{vehicle.prices.hourly}</span>
                        <span>Weekly: ₹{vehicle.prices.weekly}</span>
                        <span>Monthly: ₹{vehicle.prices.monthly}</span>
                      </div>
                      <p className="book-vehicle-description">{vehicle.description}</p>
                      <button
                        onClick={() => openBookingModal(vehicle)}
                        className="book-vehicle-button hover-scale"
                        disabled={vehicle.availability !== 'Available' || isLoading}
                      >
                        {vehicle.availability === 'Available' ? 'Book Now' : 'Not Available'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {isBookingModalOpen && (
        <div className={`book-modal-overlay ${isLoading ? 'book-modal-overlay-disabled' : ''}`} onClick={isLoading ? null : closeBookingModal}>
          <div className="book-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="book-modal-title">Book {selectedVehicle?.name}</h2>
            <div className="book-auth-options">
              {!isLoggedIn && (
                <>
                  <label>
                    <input
                      type="radio"
                      name="bookingType"
                      checked={bookingAsGuest}
                      onChange={() => setBookingAsGuest(true)}
                      disabled={isLoading}
                    />
                    Book as Guest
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="bookingType"
                      checked={!bookingAsGuest}
                      onChange={() => setBookingAsGuest(false)}
                      disabled={isLoading}
                    />
                    {isLoggedIn ? 'Book as Registered User' : 'Login and Book'}
                  </label>
                </>
              )}
            </div>
            
            {!bookingAsGuest && !isLoggedIn ? (
              <div className="book-login-prompt">
                <p>Please log in to book as a registered user.</p>
                <button
                  type="button"
                  onClick={handleLoginRedirect}
                  className="book-modal-button book-modal-submit"
                  disabled={isLoading}
                >
                  Go to Login
                </button>
              </div>
            ) : (
              <form onSubmit={handleBookingSubmit}>
                <div className="book-modal-date-time">
                  <h3>Booking Details ({priceFilterType.charAt(0).toUpperCase() + priceFilterType.slice(1)})</h3>
                  <div className="book-modal-field">
                    <label>Date From *</label>
                    <input
                      type="date"
                      name="dateFrom"
                      value={formData.dateFrom}
                      onChange={handleFormChange}
                      min={defaultDateFrom}
                      disabled={isLoading || isDateDisabled(new Date(formData.dateFrom))}
                      className={formErrors.dateFrom ? 'book-modal-error' : ''}
                    />
                    {formErrors.dateFrom && <span className="book-modal-error-text">{formErrors.dateFrom}</span>}
                  </div>
                  <div className="book-modal-field">
                    <label>Time From *</label>
                    <TimeSelect
                      name="timeFrom"
                      value={timeDisplay.timeFrom}
                      onChange={handleFormChange}
                      disabled={isLoading}
                      className={formErrors.timeFrom ? 'book-modal-error' : ''}
                    />
                    {formErrors.timeFrom && <span className="book-modal-error-text">{formErrors.timeFrom}</span>}
                  </div>
                  {priceFilterType === 'hourly' && (
                    <>
                      <div className="book-modal-field">
                        <label>Date To *</label>
                        <input
                          type="date"
                          name="dateTo"
                          value={formData.dateTo}
                          onChange={handleFormChange}
                          min={formData.dateFrom}
                          disabled={isLoading || isDateDisabled(new Date(formData.dateTo))}
                          className={formErrors.dateTo ? 'book-modal-error' : ''}
                        />
                        {formErrors.dateTo && <span className="book-modal-error-text">{formErrors.dateTo}</span>}
                      </div>
                      <div className="book-modal-field">
                        <label>Time To *</label>
                        <TimeSelect
                          name="timeTo"
                          value={timeDisplay.timeTo}
                          onChange={handleFormChange}
                          disabled={isLoading}
                          className={formErrors.timeTo ? 'book-modal-error' : ''}
                        />
                        {formErrors.timeTo && <span className="book-modal-error-text">{formErrors.timeTo}</span>}
                      </div>
                    </>
                  )}
                </div>
                <div className="book-modal-grid">
                  <div className="book-modal-field book-modal-name-address">
                    <label>Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      disabled={isLoading}
                      className={formErrors.name ? 'book-modal-error' : ''}
                    />
                    {formErrors.name && <span className="book-modal-error-text">{formErrors.name}</span>}
                  </div>
                  <div className="book-modal-field book-modal-name-address">
                    <label>Address *</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleFormChange}
                      disabled={isLoading}
                      className={formErrors.address ? 'book-modal-error' : ''}
                    />
                    {formErrors.address && <span className="book-modal-error-text">{formErrors.address}</span>}
                  </div>
                  <div className="book-modal-field">
                    <label>Contact (10-digit) *</label>
                    <input
                      type="text"
                      name="contact"
                      value={formData.contact}
                      onChange={handleFormChange}
                      disabled={isLoading}
                      className={formErrors.contact ? 'book-modal-error' : ''}
                    />
                    {formErrors.contact && <span className="book-modal-error-text">{formErrors.contact}</span>}
                  </div>
                  <div className="book-modal-field">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      disabled={isLoading}
                      className={formErrors.email ? 'book-modal-error' : ''}
                    />
                    {formErrors.email && <span className="book-modal-error-text">{formErrors.email}</span>}
                  </div>
                  <div className="book-modal-field book-modal-checkbox book-modal-full-width">
                    <label>
                      <input
                        type="checkbox"
                        name="hasLicense"
                        checked={formData.hasLicense}
                        onChange={handleFormChange}
                        disabled={isLoading}
                      />
                      I have a valid license *
                    </label>
                    {formErrors.hasLicense && <span className="book-modal-error-text">{formErrors.hasLicense}</span>}
                  </div>
                  <div className="book-modal-field">
                    <label>Upload License *</label>
                    <input
                      type="file"
                      name="licenseFile"
                      accept="image/*,application/pdf"
                      onChange={handleFormChange}
                      disabled={isLoading}
                      className={formErrors.licenseFile ? 'book-modal-error' : ''}
                    />
                    {formData.licenseFile && (
                      <div className="book-file-info">
                        <span>{formData.licenseFile.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile('licenseFile')}
                          className="book-remove-file"
                          disabled={isLoading}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {formErrors.licenseFile && <span className="book-modal-error-text">{formErrors.licenseFile}</span>}
                  </div>
                  <div className="book-modal-field">
                    <label>Upload Aadhar *</label>
                    <input
                      type="file"
                      name="aadharFile"
                      accept="image/*,application/pdf"
                      onChange={handleFormChange}
                      disabled={isLoading}
                      className={formErrors.aadharFile ? 'book-modal-error' : ''}
                    />
                    {formData.aadharFile && (
                      <div className="book-file-info">
                        <span>{formData.aadharFile.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile('aadharFile')}
                          className="book-remove-file"
                          disabled={isLoading}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {formErrors.aadharFile && <span className="book-modal-error-text">{formErrors.aadharFile}</span>}
                  </div>
                  <div className="book-modal-field book-modal-checkbox book-modal-full-width">
                    <label>
                      <input
                        type="checkbox"
                        name="agreeTerms"
                        checked={formData.agreeTerms}
                        onChange={handleFormChange}
                        disabled={isLoading}
                      />
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={downloadTermsImage}
                        className="book-terms-link"
                        disabled={isLoading}
                      >
                        terms and conditions
                      </button>{' '}
                      *
                    </label>
                    {formErrors.agreeTerms && <span className="book-modal-error-text">{formErrors.agreeTerms}</span>}
                  </div>
                </div>
                <div className="book-modal-price">
                  <p>
                    Estimated Duration: {calculateDuration(
                      formData.dateFrom,
                      priceFilterType === 'hourly' ? formData.dateTo : calculateToDate(formData.dateFrom, priceFilterType),
                      formData.timeFrom,
                      priceFilterType === 'hourly' ? formData.timeTo : formData.timeFrom
                    )} {priceFilterType}
                  </p>
                  <p>
                    Total Price (with 5% GST): ₹{calculateTotalPrice(
                      formData.dateFrom,
                      priceFilterType === 'hourly' ? formData.dateTo : calculateToDate(formData.dateFrom, priceFilterType),
                      formData.timeFrom,
                      priceFilterType === 'hourly' ? formData.timeTo : formData.timeFrom
                    )}
                  </p>
                </div>
                <div className="book-modal-actions">
                  <button
                    type="button"
                    onClick={closeBookingModal}
                    className="book-modal-button book-modal-cancel"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`book-modal-button book-modal-submit ${isLoading ? 'book-modal-loading' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="book-modal-spinner"></span>
                        Processing...
                      </>
                    ) : (
                      'Book Now'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleWhatsAppBooking}
                    className="book-modal-button book-modal-whatsapp"
                    disabled={isLoading}
                  >
                    <FaWhatsapp size={20} />
                    Book via WhatsApp
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {isSuccessModalOpen && (
        <div className="book-modal-overlay" onClick={closeSuccessModal}>
          <div className="book-modal book-success-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="book-modal-title">Booking Successful!</h2>
            <div className="book-success-details">
              <p><strong>Order ID:</strong> {orderId || 'N/A'}</p>
              <p><strong>Vehicle:</strong> {selectedVehicle?.name}</p>
              <p><strong>Name:</strong> {formData.name}</p>
              <p><strong>Address:</strong> {formData.address}</p>
              <p><strong>Contact:</strong> {formData.contact}</p>
              <p><strong>Email:</strong> {formData.email || 'N/A'}</p>
              <p>
                <strong>Rental Period:</strong> {formData.dateFrom} {formData.timeFrom} to{' '}
                {priceFilterType === 'hourly' ? formData.dateTo : calculateToDate(formData.dateFrom, priceFilterType)}{' '}
                {priceFilterType === 'hourly' ? formData.timeTo : formData.timeFrom}
              </p>
              <p>
                <strong>Estimated Duration:</strong> {calculateDuration(
                  formData.dateFrom,
                  priceFilterType === 'hourly' ? formData.dateTo : calculateToDate(formData.dateFrom, priceFilterType),
                  formData.timeFrom,
                  priceFilterType === 'hourly' ? formData.timeTo : formData.timeFrom
                )} {priceFilterType}
              </p>
              <p>
                <strong>Total Price (with 5% GST):</strong> ₹{calculateTotalPrice(
                  formData.dateFrom,
                  priceFilterType === 'hourly' ? formData.dateTo : calculateToDate(formData.dateFrom, priceFilterType),
                  formData.timeFrom,
                  priceFilterType === 'hourly' ? formData.timeTo : formData.timeFrom
                )}
              </p>
              <p className="book-success-message">
                A confirmation email has been sent to your email address/whatsapp number. Our team will contact you
                shortly for updates, confirmation, and payment.
              </p>
            </div>
            <button
              onClick={closeSuccessModal}
              className="book-modal-button book-modal-submit"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </main>
  );
}

export default BookNow;